import { todayPT } from "@homebound/activesupport";
import {
  ReadyPlanOptionGroupPersonalizationType as PersonalizationType,
  CheckoutConfigOptionGroupStep as Step,
} from "src/generated/graphqlTypes";
import {
  SelectOptionsStep_CheckoutConfigFragment,
  SelectOptionsStep_OptionGroupFragment,
} from "./1.SelectOptionsStep.generated";

/** What the user picked in one option group's card. */
export type SelectOptionsFormInput = {
  /** The `ReadyPlanOptionGroup` id, which is what `parentGroup` / `childGroup` point at. */
  groupId: string;
  /** Whether the group's checkbox is on. Cards without one (radio lists) are always `true`. */
  selected: boolean | null | undefined;
  /** The `ReadyPlanOption` picked in the group; only saved while `selected`. */
  optionId: string | null | undefined;
};

/**
 * Whether each of the group's options is one product, i.e. flooring planks or pendants: single/linked products, or a
 * package shown as a single product. These get the card with a product thumbnail per option.
 */
export function isProductOptionGroup(group: SelectOptionsStep_OptionGroupFragment): boolean {
  const { personalizationType, displayPackageOptionsAsSingleProduct } = group.group;
  const code = personalizationType?.code;
  return (
    code === PersonalizationType.SingleProduct ||
    code === PersonalizationType.LinkedProduct ||
    (code === PersonalizationType.Package && !!displayPackageOptionsAsSingleProduct)
  );
}

/**
 * Whether the group's card has a checkbox, which like checkout's `ProductAddOnCard` is only single-option groups
 * (structural, add-ons). Everything else (Elevation, schemes, Spec Level, packages, pick-one product groups like a
 * chandelier, personalization child variants, whose on/off is their parent's) always has a pick, so it has no on/off.
 */
export function hasToggle(group: SelectOptionsStep_OptionGroupFragment): boolean {
  return !group.parentGroup && group.group.isSingleOptionGroup;
}

/**
 * The step's sections in design order, straight from the backend's `step` / `subStepNumber` (groups already arrive in
 * checkout order). Groups with a `parentGroup` are rendered inside their parent, so they never get a section of their
 * own, and empty sections are dropped.
 *
 * The interior design package groups (Interior Scheme, Spec Level) land on the first sub-step alongside the
 * personalization ones, so the sub-steps are bucketed by number rather than by step.
 *
 * ADU: every group on the ADU product offering carries `step: ADU` and no sub-step (the backend overwrites the computed
 * step for ADU plans), so the step-based mapping above yields nothing for it. Like checkout's ADU summary card, its
 * sections come from the group's global option type instead: Elevation, Exterior Scheme, Interior Scheme, Spec Level
 * and ADU Selections (everything else).
 */
export function toSelectOptionsSections(
  optionGroups: SelectOptionsStep_OptionGroupFragment[],
  interiorSubSteps: SelectOptionsStep_CheckoutConfigFragment["interiorSubSteps"],
) {
  const groups = optionGroups.filter((group) => !group.parentGroup);
  const inSteps = (steps: Step[]) => groups.filter((group) => steps.includes(group.step));
  const bySubStep = inSteps([Step.InteriorDesignPackage, Step.InteriorPersonalization])
    .filter((group) => !!group.subStepNumber)
    .groupBy((group) => group.subStepNumber!);
  const aduByCategory = inSteps([Step.Adu]).groupBy(toAduCategory);
  const sections = [
    { title: "Exterior", groups: inSteps([Step.ExteriorElevation, Step.ExteriorDesignPackage]) },
    { title: "Structural", groups: inSteps([Step.OptionsStructural]) },
    ...interiorSubSteps.map((subStep) => ({ title: subStep.displayName, groups: bySubStep[subStep.stepNumber] ?? [] })),
    { title: "Add-Ons", groups: inSteps([Step.OptionsAddon]) },
    // The ADU side's sections; a main house side has no `step: ADU` groups, so these are empty and dropped there
    ...aduCategories.map((category) => ({ title: category, groups: aduByCategory[category] ?? [] })),
  ];
  return sections.filter((section) => section.groups.nonEmpty);
}

/** Checkout's `aduGroupSections` labels, in its order. */
const aduCategories = ["Elevation", "Exterior Scheme", "Interior Scheme", "Spec Level", "ADU Selections"] as const;
type AduCategory = (typeof aduCategories)[number];

/** Checkout's `getAduGroupCategory`: which ADU section a group belongs to, from its global option type flags. */
function toAduCategory(group: SelectOptionsStep_OptionGroupFragment): AduCategory {
  const { isElevation, isExteriorScheme, isInteriorScheme, isSpecLevel } = group.group.group.type;
  if (isElevation) return "Elevation";
  if (isExteriorScheme) return "Exterior Scheme";
  if (isInteriorScheme) return "Interior Scheme";
  if (isSpecLevel) return "Spec Level";
  return "ADU Selections";
}

/**
 * Each group with its options in checkout's order: selected first, then `checkoutOrder`, price ascending, then name.
 *
 * Pass a `previous` result to keep its order (like checkout, so a new pick doesn't jump to the top); options it
 * doesn't have go after it.
 */
export function sortGroupOptions(
  groups: SelectOptionsStep_OptionGroupFragment[],
  previous: SelectOptionsStep_OptionGroupFragment[] = [],
): SelectOptionsStep_OptionGroupFragment[] {
  const previousIndex = new Map(previous.flatMap((group) => group.options.map((o, i) => [o.option.id, i] as const)));
  return groups.map((group) => ({
    ...group,
    options: group.options.sortBy((o) => [
      previousIndex.get(o.option.id) ?? Number.MAX_SAFE_INTEGER,
      o.isSelected ? 0 : 1,
      o.option.checkoutOrder ?? 0,
      o.totalPriceInCents,
      o.option.name,
    ]),
  }));
}

/**
 * Whether the group is shown on the page:
 * - hidden when the server says it can't be selected (e.g. its prerequisite options aren't picked);
 * - otherwise shown unless it's expired, and an expired group still shows when the user toggled "show expired" or
 *   when it has a selected option (so the user can see and undo it).
 */
export function showGroup(
  group: SelectOptionsStep_OptionGroupFragment,
  row: SelectOptionsFormInput | undefined,
  showExpired: boolean,
): boolean {
  if (!group.canBeSelected.allowed) return false;
  const isExpired = !!group.cutoffDate && group.cutoffDate.isBefore(todayPT());
  if (showExpired || !isExpired) return true;
  return row ? !!row.selected && !!row.optionId : group.options.some((option) => option.isSelected);
}
